import React, {
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from "react";
import {
  Box,
  Typography,
  useTheme,
  IconButton,
  Button,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ClearIcon from "@mui/icons-material/ClearRounded";
import {
  DataGrid,
  GridToolbarContainer,
} from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { useStateContext } from "contexts/ContextProvider";
import ColumnFilter from "components/ColumnFilter";
import ColumnDateFilter from "components/ColumnDateFilter";
import DataGridHeader from "components/DataGridHeader";
import useColumnFilters from "hooks/useColumnFilters";
import CEQuickFilter from "components/CEQuickFilter";
import { usePageTitle } from "hooks/usePageTitle";
import { numericComparator } from "helpers/ServiceHelpers";
import dayjs from "dayjs";
import api from "services/api";
import Overlay from "components/Overlay/Overlay";

const LinguistPaymentProfile = ({ linguistData, onClose }) => {
  usePageTitle("Linguist Payment");
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const navigate = useNavigate();
  const { showSnackbar, refreshPage } = useStateContext();

  // Filter state management
  const [openFilter, setOpenFilter] = React.useState(null);
  const [sortModel, setSortModel] = React.useState([]);
  const [filterModel, setFilterModel] = React.useState({ items: [] });

  // Extract linguistData properties with defaults to avoid errors when null
  const LinguistName = linguistData?.LinguistName || "";
  const LinguistID = linguistData?.LinguistID || null;
  const LinguistTotal = linguistData?.LinguistTotal || 0;
  const AmountLeft = linguistData?.AmountLeft ?? LinguistTotal;
  const AmountPaid = linguistData?.AmountPaid ?? 0;
  const LinguistServices = useMemo(
    () => linguistData?.LinguistServices || [],
    [linguistData?.LinguistServices],
  );

  usePageTitle("Linguist Payment: " + LinguistName);

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
  };

  const handleCopyAmount = () => {
    navigator.clipboard.writeText(AmountLeft.toFixed(2));
    showSnackbar(`Copied left: $${AmountLeft.toFixed(2)}`, theme);
  };

  const handleCopyName = () => {
    navigator.clipboard.writeText(LinguistName);
    showSnackbar(`Copied: ${LinguistName}`, theme);
  };

  const handleOpenLinguistProfile = () => {
    navigate(`/linguists/${LinguistID}`);
  };

  const handleMarkAsPaid = async (serviceId) => {
    try {
      const idsToMark = Array.isArray(serviceId) ? serviceId : [serviceId];
      const validIds = idsToMark.filter(Boolean);
      if (validIds.length === 0) {
        showSnackbar("No unpaid services to mark", theme);
        return;
      }
      await api.put("/services/mark-linguist-paid", {
        serviceIds: validIds,
      });
      showSnackbar(`${validIds.length} service(s) marked as paid`, theme);
      // Refresh parent data
      if (refreshPage) {
        refreshPage();
      }
      onClose && onClose(true);
    } catch (error) {
      console.error("Error marking service as paid:", error);
      showSnackbar(
        error.response?.data?.error || "Failed to mark service as paid",
        theme,
      );
    }
  };

  const handleServiceRowClick = (params) => {
    const serviceId = params.id;
    const category = params.row.ServiceCategory;

    // Route based on service category
    if (category === "Interpretation" || category === "OPI") {
      navigate(`/interpretations/${serviceId}`);
    } else if (category === "Translation") {
      navigate(`/translations/${serviceId}`);
    } else if (category === "Other Service") {
      navigate(`/otherServices/${serviceId}`);
    }
  };

  // Helper function to generate localStorage key for column filters
  const getFilterStorageKey = useCallback((fieldName) => {
    const upperFieldName = fieldName.toUpperCase();
    return `LINGUIST_PAYMENT_PROFILE_${LinguistID}_FILTER_${upperFieldName}`;
  }, [LinguistID]);

  // Helper function to save a column filter to localStorage
  const saveColumnFilter = useCallback((fieldName, values) => {
    const key = getFilterStorageKey(fieldName);
    if (values && values.length > 0) {
      localStorage.setItem(key, JSON.stringify(values));
    } else {
      localStorage.removeItem(key);
    }
  }, [getFilterStorageKey]);

  // Helper function to load a column filter from localStorage
  const loadColumnFilter = useCallback((fieldName) => {
    const key = getFilterStorageKey(fieldName);
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : [];
  }, [getFilterStorageKey]);

  // Helper function to load all saved column filters
  const loadAllColumnFilters = useCallback(() => {
    const savedFilters = {};
    const fields = [
      "ServiceCategory",
      "ProjectNumber",
      "Details",
      "Date",
      "TotalLinguistPay",
      "LinguistPaid",
      "Status",
    ];

    fields.forEach((field) => {
      const values = loadColumnFilter(field);
      if (values.length > 0) {
        savedFilters[field] = values;
      }
    });

    return savedFilters;
  }, [loadColumnFilter]);

  // Helper function to clear all column filters from localStorage
  const clearAllColumnFilters = useCallback(() => {
    const fields = [
      "ServiceCategory",
      "ProjectNumber",
      "Details",
      "Date",
      "TotalLinguistPay",
      "LinguistPaid",
      "Status",
    ];
    fields.forEach((field) => {
      localStorage.removeItem(getFilterStorageKey(field));
    });
  }, [getFilterStorageKey]);

  // Services data rows
  const servicesRows = useMemo(
    () =>
      (LinguistServices || []).map((service) => {
        const category = service.ServiceCategory;
        let status = "";
        if (category === "Interpretation" || category === "OPI") {
          status = service.InterpretationStatus || "";
        } else if (category === "Translation") {
          status = service.TranslationStatus || "";
        } else if (category === "Other Service") {
          status = service.OtherServiceStatus || "";
        } else {
          status =
            service.InterpretationStatus ||
            service.OtherServiceStatus ||
            service.TranslationStatus ||
            "";
        }
        return {
          id: service.id,
          ServiceCategory: category,
          ProjectNumber: service.ProjectNumber,
          Details: service.Details || "N/A",
          Date: service.Date,
          TotalLinguistPay: service.TotalLinguistPay || 0,
          LinguistPaid: !!service.LinguistPaid,
          Status: status,
        };
      }),
    [LinguistServices],
  );

  const unpaidServiceIds = useMemo(
    () => servicesRows.filter((s) => !s.LinguistPaid).map((s) => s.id),
    [servicesRows],
  );
  const hasUnpaidServices = unpaidServiceIds.length > 0;

  // Filter columns configuration for cross-filtering
  const filterColumns = useMemo(
    () => [
      {
        field: "ServiceCategory",
        valueGetter: (row) => row.ServiceCategory || "",
      },
      { field: "ProjectNumber", valueGetter: (row) => row.ProjectNumber || "" },
      { field: "Details", valueGetter: (row) => row.Details || "N/A" },
      {
        field: "Date",
        valueGetter: (row) =>
          row.Date ? dayjs.tz(row.Date).format("M/D/YYYY") : "N/A",
      },
      {
        field: "TotalLinguistPay",
        valueGetter: (row) =>
          row.TotalLinguistPay
            ? `$${parseFloat(row.TotalLinguistPay).toFixed(2)}`
            : "$0.00",
      },
      {
        field: "LinguistPaid",
        valueGetter: (row) => (row.LinguistPaid ? "Yes" : "No"),
      },
      {
        field: "Status",
        valueGetter: (row) => row.Status || "",
      },
    ],
    [],
  );

  // Column filters using custom hook
  const {
    filteredData: columnFilteredData,
    columnFilters,
    handleFilterChange: handleColumnFilterChange,
    clearAllFilters,
    hasActiveFilters,
  } = useColumnFilters(servicesRows || [], filterColumns);

  const handleColumnFilterChangeRef = useRef(handleColumnFilterChange);
  const hasAppliedSavedFiltersRef = useRef(false);

  useEffect(() => {
    handleColumnFilterChangeRef.current = handleColumnFilterChange;
  }, [handleColumnFilterChange]);

  // Wrapped filter change handler that saves to localStorage
  const handleColumnFilterChangeWithStorage = useCallback(
    (field, values) => {
      handleColumnFilterChange(field, values);
      saveColumnFilter(field, values);
    },
    [handleColumnFilterChange, saveColumnFilter],
  );

  // Wrapped clear all filters that also clears localStorage
  const handleClearAllFilters = useCallback(() => {
    clearAllFilters();
    clearAllColumnFilters();
    setFilterModel({ items: [] });
  }, [clearAllFilters, clearAllColumnFilters]);

  // Check if quick filter has values
  const hasQuickFilter = filterModel?.quickFilterValues?.length > 0;

  // Combined check for any active filters
  const hasAnyActiveFilters = hasActiveFilters || hasQuickFilter;

  // Load saved column filters on mount and apply them
  useEffect(() => {
    if (!servicesRows || servicesRows.length === 0) {
      hasAppliedSavedFiltersRef.current = false;
      return;
    }

    if (hasAppliedSavedFiltersRef.current) {
      return;
    }

    const savedFilters = loadAllColumnFilters();
    Object.entries(savedFilters).forEach(([field, values]) => {
      handleColumnFilterChangeRef.current(field, values);
    });

    hasAppliedSavedFiltersRef.current = true;
  }, [servicesRows, loadAllColumnFilters]);

  // Cross-filtering data calculation
  const filterData = useMemo(() => {
    return filterColumns.map((column) => {
      const otherFilters = Object.entries(columnFilters).filter(
        ([field]) => field !== column.field,
      );

      if (otherFilters.length === 0) {
        return servicesRows || [];
      }

      return (servicesRows || []).filter((row) => {
        return otherFilters.every(([field, selectedValues]) => {
          if (!selectedValues || selectedValues.length === 0) return true;
          const filterColumn = filterColumns.find((col) => col.field === field);
          if (!filterColumn) return true;
          const value = filterColumn.valueGetter(row);
          return selectedValues.includes(value);
        });
      });
    });
  }, [columnFilters, servicesRows, filterColumns]);

  // keep toolbar identity stable so quick filter input retains focus
  const clearAllFiltersRef = useRef(handleClearAllFilters);
  useEffect(() => {
    clearAllFiltersRef.current = handleClearAllFilters;
  }, [handleClearAllFilters]);

  const handleToolbarClear = useCallback(() => {
    clearAllFiltersRef.current();
  }, []);

  const CustomToolbar = useMemo(() => {
    const Toolbar = ({ hasAnyActiveFilters, onClear, theme }) => (
      <GridToolbarContainer
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box sx={{ flex: 1 }} />

        <Box
          sx={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            alignItems: "center",
          }}
        >
          {hasAnyActiveFilters && (
            <Button
              startIcon={<ClearIcon />}
              onClick={onClear}
              variant="outlined"
              size="small"
              sx={{
                color: theme.palette.primary.main,
                borderColor: theme.palette.primary.main,
                "&:hover": {
                  borderColor: theme.palette.primary.buttonHoverSelected,
                  backgroundColor: theme.palette.primary.buttonHoverSelected,
                  color: theme.palette.background.default,
                },
              }}
            >
              Clear Filters
            </Button>
          )}
        </Box>

        <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
          <CEQuickFilter />
        </Box>
      </GridToolbarContainer>
    );

    return Toolbar;
  }, []);

  // Early return after all hooks
  if (!linguistData) return null;

  // Services DataGrid columns
  const servicesColumns = [
    {
      field: "ServiceCategory",
      headerName: "Category",
      flex: 1,
      minWidth: 100,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[0] || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "ProjectNumber",
      headerName: "Project #",
      flex: 1,
      minWidth: 100,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[1] || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Details",
      headerName: "Details",
      flex: 3,
      minWidth: 300,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[2] || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Date",
      headerName: "Date",
      flex: 1,
      minWidth: 100,
      valueGetter: (_, params) => {
        return params.Date ? dayjs.tz(params.Date).format("M/D/YYYY") : "N/A";
      },
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnDateFilter
            data={servicesRows || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "TotalLinguistPay",
      headerName: "Amount",
      sortComparator: numericComparator,
      flex: 1,
      minWidth: 100,
      valueGetter: (_, params) => {
        return params.TotalLinguistPay
          ? `$${parseFloat(params.TotalLinguistPay).toFixed(2)}`
          : "$0.00";
      },
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[4] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "LinguistPaid",
      headerName: "Linguist Paid",
      flex: 1,
      minWidth: 140,
      valueGetter: (_, params) => (params.LinguistPaid ? "Yes" : "No"),
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              color: params.row.LinguistPaid
                ? theme.palette.primary.main
                : theme.palette.error.main,
            }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[5] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Status",
      headerName: "Status",
      flex: 1.5,
      minWidth: 150,
      renderCell: (params) => {
        const status = params.row.Status || "";
        let color = theme.palette.primary.text2;
        if (status.startsWith("Confirmed")) {
          color = theme.palette.warning.main;
        } else if (
          status === "Completed" ||
          status === "Delivered" ||
          status === "Published"
        ) {
          color = theme.palette.primary.main;
        } else if (status === "Cancelled" || status === "No Show") {
          color = theme.palette.error.main;
        }
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              width: "100%",
            }}
          >
            <Typography sx={{ fontWeight: status ? 600 : 400, color }}>
              {status || "—"}
            </Typography>
          </Box>
        );
      },
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[6] || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
  ];

  const footerActions = (
    <Box sx={{ display: "flex", gap: 1, width: "100%", justifyContent: "flex-end" }}>
      {isMobile ? (
        <>
          <Tooltip title="Open Profile" placement="top">
            <IconButton
              color="primary"
              onClick={handleOpenLinguistProfile}
              sx={{
                width: "40px",
                height: "40px",
                border: `2px solid ${theme.palette.primary.main}`,
                borderRadius: "12px",
                "&:hover": {
                  backgroundColor: theme.palette.primary.light,
                },
              }}
            >
              <PersonRoundedIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Copy Name" placement="top">
            <IconButton
              color="primary"
              onClick={handleCopyName}
              sx={{
                width: "40px",
                height: "40px",
                border: `2px solid ${theme.palette.primary.main}`,
                borderRadius: "12px",
                "&:hover": {
                  backgroundColor: theme.palette.primary.light,
                },
              }}
            >
              <ContentCopyRoundedIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Copy Amount" placement="top">
            <IconButton
              color="primary"
              onClick={handleCopyAmount}
              sx={{
                width: "40px",
                height: "40px",
                border: `2px solid ${theme.palette.primary.main}`,
                borderRadius: "12px",
                "&:hover": {
                  backgroundColor: theme.palette.primary.light,
                },
              }}
            >
              <ContentCopyRoundedIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Mark as Paid" placement="top">
            <span>
              <IconButton
                disabled={!hasUnpaidServices}
                onClick={() => handleMarkAsPaid(unpaidServiceIds)}
                sx={{
                  width: "40px",
                  height: "40px",
                  backgroundColor: theme.palette.primary.main,
                  color: "#fff",
                  borderRadius: "12px",
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                  "&.Mui-disabled": {
                    backgroundColor:
                      theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                  },
                }}
              >
                <CheckCircleRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>
        </>
      ) : (
        <>
          <Button
            color="primary"
            variant="outlined"
            size="medium"
            startIcon={<PersonRoundedIcon />}
            onClick={handleOpenLinguistProfile}
            sx={{
              borderRadius: "12px",
            }}
          >
            Open Profile
          </Button>

          <Button
            color="primary"
            variant="outlined"
            size="medium"
            startIcon={<ContentCopyRoundedIcon />}
            onClick={handleCopyName}
            sx={{
              borderRadius: "12px",
            }}
          >
            Copy Name
          </Button>

          <Button
            color="primary"
            variant="outlined"
            size="medium"
            startIcon={<ContentCopyRoundedIcon />}
            onClick={handleCopyAmount}
            sx={{
              borderRadius: "12px",
            }}
          >
            Copy Amount
          </Button>

          <Button
            color="primary"
            variant="contained"
            size="medium"
            disabled={!hasUnpaidServices}
            onClick={() => handleMarkAsPaid(unpaidServiceIds)}
            sx={{
              borderRadius: "12px",
            }}
          >
            Mark as Paid
          </Button>
        </>
      )}
    </Box>
  );

  return (
    <Overlay
      id="linguist-payment-profile"
      open={true}
      onClose={onClose}
      title={LinguistName}
      footer={footerActions}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          p: "1rem 1rem",
          width: "100%",
        }}
      >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{ color: theme.palette.primary.main }}
            >
              Services ({servicesRows.length})
            </Typography>
            <Typography
              variant="h4"
              color={theme.palette.primary.main}
              fontWeight="bold"
            >
              Left: ${AmountLeft.toFixed(2)} - Paid: ${AmountPaid.toFixed(2)}
            </Typography>
          </Box>

          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              p: 0.25,
            }}
          >
            <DataGrid
              rows={columnFilteredData || []}
              columns={servicesColumns}
              pageSizeOptions={[20, 50, 100]}
              filterModel={filterModel}
              onFilterModelChange={(newFilterModel) => {
                setFilterModel(newFilterModel);
              }}
              onRowClick={(params, event, details) => {
                if (openFilter) {
                  setOpenFilter(null);
                  return;
                }
                handleServiceRowClick(params);
              }}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              disableColumnMenu
              disableColumnSorting
              rowSelectionModel={openFilter ? [] : undefined}
              disableRowSelectionOnClick={!!openFilter}
              slots={{ toolbar: CustomToolbar }}
              slotProps={{
                loadingOverlay: {
                  variant: "skeleton",
                  noRowsVariant: "skeleton",
                },
                toolbar: {
                  showQuickFilter: true,
                  hasAnyActiveFilters,
                  onClear: handleToolbarClear,
                  theme,
                  printOptions: { disableToolbarButton: true },
                  csvOptions: { disableToolbarButton: true },
                },
              }}
              ignoreDiacritics
              density="compact"
              sx={{
                backgroundColor: theme.palette.background.default,
                borderRadius: "12px",
                border: `1px solid ${theme.palette.divider}`,
                height: "55vh",
                maxHeight: "55vh",
                boxShadow: theme.shadows[2],
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: "bold",
                  color: theme.palette.primary.main,
                },
                "& .MuiDataGrid-scrollbarFiller": {
                  backgroundColor: theme.palette.background.alt,
                },
                "& .MuiDataGrid-sortIcon": {
                  display: "none !important",
                },
                "& .MuiDataGrid-menuIconButton": {
                  display: "none !important",
                },
                "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon": {
                  display: "none !important",
                },
                "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer":
                  {
                    display: "none !important",
                  },
                "& .MuiDataGrid-columnHeaderTitleContainerContent": {
                  flex: 1,
                },
                "& .MuiDataGrid-columnSeparator": {
                  color: theme.palette.primary.main,
                },
                "& .MuiButton-root": {
                  color: theme.palette.primary.main,
                },
                "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
                  outline: "none !important",
                },
                "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
                  {
                    outline: "none !important",
                  },
                "& .MuiDataGrid-row": {
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                  "&.Mui-selected": {
                    backgroundColor: theme.palette.primary.main + "20",
                    "&:hover": {
                      backgroundColor: theme.palette.primary.main + "30",
                    },
                  },
                },
              }}
            />
          </Box>
        </Box>
    </Overlay>
  );
};

export default LinguistPaymentProfile;
